import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { formatRelative } from '../format';
import type { GhlAppStatus } from '../types';

/**
 * Settings → HighLevel app. One Marketplace app, installed by the agency on
 * every sub-account, gives the dashboard and the Assistant one set of
 * permissions everywhere — instead of a pasted token per sub-account.
 */
export function GhlAppCard() {
  const [app, setApp] = useState<GhlAppStatus | null>(null);
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [scopes, setScopes] = useState<Set<string>>(new Set());
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const suggestedRedirect = `${window.location.origin}/api/auth/callback`;

  useEffect(() => {
    api
      .ghlApp()
      .then((next) => {
        setApp(next);
        setClientId(next.clientId);
        setRedirectUri(next.redirectUri || suggestedRedirect);
        setScopes(new Set(next.scopes));
      })
      .catch((cause) => setNote({ ok: false, text: cause instanceof ApiError ? cause.message : String(cause) }));
  }, [suggestedRedirect]);

  async function save() {
    setBusy(true);
    setNote(null);
    try {
      const next = await api.saveGhlApp({
        clientId: clientId.trim(),
        ...(secret.trim() ? { clientSecret: secret.trim() } : {}),
        redirectUri: redirectUri.trim(),
        scopes: [...scopes],
      });
      setApp(next);
      setSecret('');
      setNote({ ok: true, text: 'Saved. Now install the app on your sub-accounts.' });
    } catch (cause) {
      setNote({ ok: false, text: cause instanceof ApiError ? cause.message : String(cause) });
    } finally {
      setBusy(false);
    }
  }

  function toggle(scope: string) {
    setScopes((current) => {
      const next = new Set(current);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  }

  if (!app) return note ? <div className="banner error">{note.text}</div> : <p className="empty">Loading…</p>;

  const missing = app.installed ? app.scopes.filter((scope) => !app.grantedScopes.includes(scope)) : [];
  const ready = app.source !== 'none';

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <h2>HighLevel app</h2>
          <span className="tag">
            <span className="dot" style={{ background: app.installed ? 'var(--outcome-answered)' : ready ? 'var(--outcome-no_answer)' : 'var(--ink-muted)' }} />
            {app.installed ? `Installed ${formatRelative(app.installedAt)}` : ready ? 'Saved — not installed yet' : 'Not set up'}
          </span>
        </div>
        <p className="card-sub">
          A private Marketplace app, installed by your agency on every sub-account, gives the dashboard and the Assistant
          one set of permissions everywhere. Once it is installed it is used ahead of pasted Private Integration Tokens;
          those stay as a fallback for any sub-account the app is not on.
        </p>

        {app.fromEnv ? (
          <div className="inline-result">This app is configured in the server’s .env file (client ID {app.clientId}). Change it there.</div>
        ) : (
          <div className="dialog-grid">
            <div className="field full">
              <label htmlFor="app-redirect">Redirect URL — paste this into the app’s Auth settings</label>
              <div className="row nowrap-row">
                <input id="app-redirect" type="text" value={redirectUri} onChange={(event) => setRedirectUri(event.target.value)} style={{ flex: '1 1 320px' }} />
                <button type="button" className="btn btn-sm" onClick={() => navigator.clipboard?.writeText(redirectUri)}>
                  Copy
                </button>
              </div>
            </div>
            <div className="field">
              <label htmlFor="app-client-id">Client ID</label>
              <input id="app-client-id" type="text" value={clientId} onChange={(event) => setClientId(event.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="app-secret">Client secret {app.secretHint ? `(saved, ends ${app.secretHint})` : ''}</label>
              <input
                id="app-secret"
                type="password"
                autoComplete="off"
                placeholder={app.secretHint ? 'Paste a new secret to replace it' : ''}
                value={secret}
                onChange={(event) => setSecret(event.target.value)}
              />
            </div>
          </div>
        )}

        <div className="scope-columns">
          {(
            [
              ['For the reports', app.recommendedScopes.read],
              ['For the Assistant to act', app.recommendedScopes.write],
            ] as [string, string[]][]
          ).map(([title, list]) => (
            <div key={title}>
              <div className="ai-list-title">{title}</div>
              {list.map((scope) => (
                <label key={scope} className="scope-row">
                  <input type="checkbox" checked={scopes.has(scope)} disabled={app.fromEnv} onChange={() => toggle(scope)} />
                  <code>{scope}</code>
                  {app.installed && scopes.has(scope) ? (
                    app.grantedScopes.includes(scope) ? <span className="scope-ok">granted</span> : <span className="scope-missing">not granted</span>
                  ) : null}
                </label>
              ))}
            </div>
          ))}
        </div>
        <p className="small muted">
          Tick the same scopes on the app in HighLevel. Leave the “act” ones off if the Assistant should only look things up.
        </p>
        {missing.length ? (
          <div className="inline-result bad">
            The install did not grant {missing.join(', ')}. Add them to the app in HighLevel, then press Install again so
            your agency approves the new permissions.
          </div>
        ) : null}
        {note ? <div className={`inline-result ${note.ok ? 'ok' : 'bad'}`}>{note.text}</div> : null}

        <div className="row" style={{ marginTop: 12 }}>
          {!app.fromEnv ? (
            <button type="button" className="btn btn-primary" disabled={busy || !clientId.trim() || !redirectUri.trim()} onClick={save}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          ) : null}
          <a className={`btn${ready ? '' : ' disabled'}`} href={ready ? '/api/auth/install' : undefined} aria-disabled={!ready}>
            {app.installed ? 'Reinstall / update permissions' : 'Install on sub-accounts'}
          </a>
          {app.companyId ? <span className="small muted">Agency {app.companyId}</span> : null}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Creating the app in HighLevel</h2>
        </div>
        <ol className="steps">
          <li>
            Go to <strong>marketplace.gohighlevel.com</strong>, sign in with your agency login, open <strong>My Apps</strong>{' '}
            and choose <strong>Create App</strong>.
          </li>
          <li>
            Name it (e.g. “Reporting Dashboard”), set <strong>App Type: Private</strong>, <strong>Target User: Sub-account</strong>{' '}
            and <strong>Who can install: Agency only</strong>. Sub-account is what unlocks the sub-account scopes — an
            Agency-target app cannot act inside sub-accounts. Allow <strong>bulk installation</strong> if asked.
          </li>
          <li>Under <strong>Scopes</strong>, add every scope ticked above.</li>
          <li>
            Under <strong>Auth</strong>, add the <strong>Redirect URL</strong> shown above, then generate a{' '}
            <strong>Client ID</strong> and <strong>Client secret</strong> and paste them here. Save.
          </li>
          <li>
            Press <strong>Install on sub-accounts</strong>, choose your agency, select <strong>all sub-accounts</strong> and
            approve. New sub-accounts later need the app installed on them too (Agency → Marketplace → your app).
          </li>
        </ol>
      </div>
    </div>
  );
}
